/**
 * EmptyState — the product home (`/products/:pid`): a centered, ChatGPT-style
 * column. A question at the top ("What do you want to understand about
 * curium?"), the shared composer under it with the LIVE model/effort
 * pickers on its bottom edge, and, quietly below, a few suggested openers.
 *
 * Round 3 (owner: "make it look prettier this entire section. That includes
 * input field and the settings"):
 *  - the small centered product name over the box was doing no work, so the
 *    space it took now carries the greeting, which names the product AND
 *    says what the box is for;
 *  - the pickers moved inside the input surface (ComposerPickers), so the
 *    card is one object rather than a box with a tray under it;
 *  - the starter questions read as suggestions (quiet rows, a leading
 *    glyph) instead of a hedge of pills with the same weight as everything
 *    else, and they say where they come from: the flows in the map, picked
 *    client-side, no model call and no cost (owner: "How are these
 *    generated?").
 *
 * Round 7 (owner: "The input text field can be much bigger and wider") widened
 * the column to the measure the composer itself now carries, and rests the box
 * three lines tall here: on this page it is the only thing being asked for.
 * The centred cap stays HERE and only here (`width="measure"`, the default):
 * this page is a wide empty canvas, and a field bled across all of it reads as
 * a text editor rather than as a place to ask one question. The in-session
 * composer takes the full width of its column instead.
 *
 * Model/effort are staged locally and sent as explicit overrides only, in
 * the session create body `{model?, effort?}` (omitted = inherit product ??
 * account ?? server defaults).
 *
 * Sending (or tapping a suggestion) creates the session, fires the first
 * turn, and navigates to `/products/:pid/chats/:sid` — the optimistic echo is already
 * staged by startTurn, so the session page opens mid-thought, never blank.
 * A failed create keeps the text in the box and says why, inline.
 */

import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { useArtifact, useCreateSession, useMe, useProduct } from '../../api/queries';
import type { ActiveJobRef, Effort, Ulid } from '../../api/types';
import { sessionPath } from '../../lib/deepLink';
import { Button, ErrorNote, SkeletonLine } from '../../lib/ui';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { ChangesBanner } from '../changes';
import { ChatComposer, HOME_COMPOSER_MIN_ROWS } from '../chat/NewComposer';
import { ComposerPickers } from '../chat/ComposerPickers';
import { FALLBACK_EFFORT, FALLBACK_MODEL } from '../chat/labels';
import { startTurn } from '../chat/streamController';
import { CostPreviewDialog } from '../refresh/CostPreviewDialog';
import { selectRepoActiveJob, selectRepoRefreshing, useRefreshJobs } from '../refresh/refreshJobsStore';
import { readingLine } from './readingLine';
import { starterQuestions, type FlowsArtifact } from './starterQuestions';

/** The connect wizard loads when it first opens (2026-09-08): the product home is on every visit's path, the wizard on a new product's only. */
const AddRepoWizard = lazy(() =>
  import('../products/AddRepoWizard').then((m) => ({ default: m.AddRepoWizard })),
);

/** A server-reported job that is actually running (or about to). */
function jobUnderway(job: ActiveJobRef | null | undefined): boolean {
  return job !== null && job !== undefined && (job.status === 'running' || job.status === 'queued');
}

/** Fallback when neither the product nor the account carries a default. */
export const DEFAULT_MODEL_LABEL = FALLBACK_MODEL;

/** The leading mark on a suggestion row: a spark, not a bullet. */
function SuggestionGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-0.5 shrink-0 text-tertiary"
      aria-hidden
    >
      <path d="M12 3.5 13.9 9l5.6 2-5.6 2-1.9 5.5L10.1 13 4.5 11l5.6-2z" />
    </svg>
  );
}

export function EmptyState() {
  const { pid = '' } = useParams<'pid'>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const product = useProduct(pid);
  const me = useMe();
  const createSession = useCreateSession(pid);
  const [error, setError] = useState<string | null>(null);
  // Staged per-session overrides — sent only when explicitly chosen.
  const [model, setModel] = useState<string | null>(null);
  const [effort, setEffort] = useState<Effort | null>(null);
  // Palette ask-guide (and friends) stage a draft here; the composer adopts it.
  const composerDraft = useChatStore((s) => s.composerDraft);

  const repos = product.data?.repos ?? [];
  // Connecting code is the second screen of making a product (2026-09-05;
  // owner: "Should we just have 2nd screen???"): the create dialog lands
  // here with `connect` in the navigation state and the wizard opens at
  // once; the button below opens the same wizard in place, not a page.
  const location = useLocation();
  const [connecting, setConnecting] = useState<boolean>(
    (location.state as { connect?: boolean } | null)?.connect === true,
  );
  const remembered = useUiStore((s) => s.activeProjectId);
  const repoId: Ulid | undefined =
    remembered !== null && repos.some((r) => r.id === remembered)
      ? remembered
      : repos[0]?.id;
  const flows = useArtifact<FlowsArtifact>(repoId, 'flows');

  // A repo whose map is being read RIGHT NOW. The server rows (useProduct)
  // are the truth; the tracked-jobs store fills the gap between the wizard
  // kicking the first extract and the next product fetch knowing about it.
  const trackedRepoId = useRefreshJobs(
    (s) => repos.find((r) => selectRepoRefreshing(s, r.id))?.id ?? null,
  );
  const readingRepo =
    repos.find((r) => jobUnderway(r.active_job)) ??
    repos.find((r) => r.id === trackedRepoId) ??
    null;
  // The live fold of that job (phase, counts), when this tab follows it.
  const readingJobState = useRefreshJobs((s) =>
    readingRepo !== null ? (selectRepoActiveJob(s, readingRepo.id)?.state ?? null) : null,
  );
  // Read but not written: the wizard's free extract has landed a graph and
  // nothing has been narrated (no flows either, which is what a summary can
  // see). The one thing to do is write the map, price shown first.
  const unwrittenRepo =
    readingRepo === null
      ? (repos.find((r) => r.node_count !== null && r.flow_count === null) ?? null)
      : null;
  const [writeMapFor, setWriteMapFor] = useState<Ulid | null>(null);

  // The resolved defaults the "Default (…)" options describe.
  const defaultModel =
    product.data?.default_model ?? me.data?.default_model ?? DEFAULT_MODEL_LABEL;
  const defaultEffort: Effort =
    product.data?.default_effort ?? me.data?.default_effort ?? FALLBACK_EFFORT;
  const productName = product.data?.name ?? '';

  const ask = async (text: string): Promise<boolean> => {
    setError(null);
    try {
      // Only explicit overrides ride the create body (DECISIONS #62).
      const { session_id } = await createSession.mutateAsync({
        ...(model !== null ? { model } : {}),
        ...(effort !== null ? { effort } : {}),
      });
      useChatStore.getState().setActiveSession(session_id);
      void startTurn(qc, session_id, text);
      void navigate(sessionPath(pid, session_id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the conversation.');
      return false;
    }
  };

  const questions = starterQuestions([flows.data]);

  if (product.isPending) {
    return (
      <div className="mx-auto w-full max-w-[calc(var(--measure-home)+3rem)] px-6 pt-[18vh]">
        <SkeletonLine width="30%" height={16} />
        <div className="h-4" />
        <SkeletonLine height={96} />
      </div>
    );
  }
  if (product.isError) {
    return (
      <div className="mx-auto w-full max-w-[calc(var(--measure-home)+3rem)] px-6 pt-[18vh]" data-testid="home-product-error">
        <ErrorNote>Could not load the product: {product.error.message}</ErrorNote>
        <p className="mt-4 text-secondary">
          It may belong to another account or have been removed.{' '}
          <Link to="/products" className="text-(--accent) underline underline-offset-2">
            ← All products
          </Link>
        </p>
      </div>
    );
  }

  const controls = (
    <ComposerPickers
      model={model}
      effort={effort}
      defaultModel={defaultModel}
      defaultEffort={defaultEffort}
      onModel={setModel}
      onEffort={setEffort}
      modelTestId="composer-model-picker"
      effortTestId="composer-effort-meter"
    />
  );

  return (
    <div data-testid="product-home" className="flex h-full flex-col overflow-y-auto">
      {/* Mounted above the connected/unconnected branch on purpose: the
          wizard creates the repo, the product refetches, and the branch below
          flips. Inside the branch the wizard unmounted at that moment, before
          its key step ran and before it started the free extract, leaving a
          codebase with no map and a composer offered against nothing
          (audit 2026-09-07). Mounted only while open: its close resets its
          state anyway, and its chunk loads on the first open. */}
      {connecting ? (
        <Suspense fallback={null}>
          <AddRepoWizard productId={pid} open onClose={() => setConnecting(false)} />
        </Suspense>
      ) : null}
      {/* The column is the composer's measure plus its own px-6 (3rem across
          both sides), so the box inside lands at exactly --measure-home and
          everything above and below it shares the composer's edges. */}
      <div className="mx-auto w-full max-w-[calc(var(--measure-home)+3rem)] px-6 pt-[18vh] pb-12">
        {/* Coming back after a while, this is where you land, so this is
            where "your code moved" is news. It renders only when something
            actually changed, and only until you look. */}
        {repoId !== undefined ? <ChangesBanner productId={pid} repoId={repoId} framed /> : null}
        <h1
          data-testid="home-greeting"
          className="mt-0 mb-4 text-center text-[length:var(--text-section-size)] leading-[var(--text-section-line)] font-semibold text-primary"
        >
          {productName.length > 0
            ? `What do you want to understand about ${productName}?`
            : 'What do you want to understand?'}
        </h1>
        {repos.length === 0 ? (
          // With no code connected there is nothing a question can run
          // against; an autofocused composer here invited spending a turn on
          // nothing. The one useful act is connecting code, so that is the
          // ONE control this state offers.
          <div data-testid="home-connect" className="flex flex-col items-center gap-4">
            <button
              type="button"
              data-testid="home-connect-cta"
              onClick={() => setConnecting(true)}
              className="inline-flex h-9 cursor-pointer items-center rounded-card border border-transparent bg-(--accent) px-4 font-medium text-(--bg-canvas) transition-opacity duration-(--motion-hover) hover:opacity-90"
            >
              Connect a codebase
            </button>
            <p
              data-testid="home-no-repos"
              className="mt-0 mb-0 max-w-[46ch] text-center text-tertiary"
            >
              Nothing is connected to this product yet. A developer connects the code once,
              and loremap draws the map the guide teaches from.
            </p>
          </div>
        ) : (
          <>
            {readingRepo !== null ? (
              // The first minutes of a fresh codebase: the map is being
              // drawn, questions already work, and this one line says what
              // is happening, how far along, and that the reader can ask.
              <p data-testid="home-reading" className="mt-0 mb-2 text-center text-tertiary">
                {readingLine(readingRepo.name, readingJobState)}
              </p>
            ) : unwrittenRepo !== null ? (
              <div
                data-testid="home-unwritten"
                className="mb-3 flex flex-col items-center gap-2 text-center"
              >
                <p className="mt-0 mb-0 text-tertiary">
                  {unwrittenRepo.name} is read; the map is not written yet.
                </p>
                <Button
                  kind="default"
                  testId="home-write-map"
                  onClick={() => setWriteMapFor(unwrittenRepo.id)}
                >
                  Write the map
                </Button>
              </div>
            ) : null}
            {writeMapFor !== null ? (
              <CostPreviewDialog
                repoId={writeMapFor}
                open
                onClose={() => setWriteMapFor(null)}
              />
            ) : null}
            <ChatComposer
              placeholder="Ask anything…"
              onSend={ask}
              disabled={createSession.isPending}
              controls={controls}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the home page is the composer: the question is the one thing to do here
              autoFocus
              minRows={HOME_COMPOSER_MIN_ROWS}
              draft={composerDraft}
              onDraftConsumed={() => useChatStore.getState().setComposerDraft(null)}
            />
            {error !== null ? (
              <p data-testid="home-create-error" className="mt-2 mb-0 text-(--danger)">
                {error}
              </p>
            ) : null}
            {questions.length > 0 ? (
          <div data-testid="starter-questions" className="mt-6">
            <p
              className="mt-0 mb-1 px-2 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary"
              title="These come from the flows loremap found in your codebase. Picked here in the browser, so they cost nothing"
            >
              Suggested from your map
            </p>
            <div className="flex flex-col">
              {questions.map((q) => (
                <button
                  key={q}
                  type="button"
                  data-testid="starter-chip"
                  disabled={createSession.isPending}
                  onClick={() => void ask(q)}
                  className="flex w-full cursor-pointer items-start gap-2 rounded-card border border-transparent bg-transparent px-2 py-2 text-left text-[length:var(--text-ui-size)] text-secondary transition-colors duration-(--motion-hover) hover:bg-raised hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SuggestionGlyph />
                  <span className="min-w-0 flex-1">{q}</span>
                </button>
              ))}
            </div>
          </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
